import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import useBookByPriceStore, {
  DEFAULT_BOOK_BY_PRICE_STATE,
} from "../../store/bookByPriceStore";
import { useThemeStore } from "../../store/themeStore";
import { isDarkTheme } from "../../config/themes";
import { useGroupLink } from "../../hooks/useGroupLink";
import { useBookByPriceSnapshot } from "./hooks/useBookByPriceSnapshot";
import { useBookOrderDrag } from "./hooks/useBookOrderDrag";
import { useOrderOverlay } from "../../hooks/useOrderOverlay";
import { usePositionData } from "../../hooks/usePositionData";
import {
  useOrderDefaults,
  useOrderDefaultsStore,
} from "../../hooks/useOrderDefaults";
// import { useRouterStatus } from "../../hooks/useRouterStatus"; // SuperDOM sempre habilitado (gate router-offline desativado)
import { STATUS } from "./hooks/bookByPriceHelpers";
import BookByPriceTraditionalView from "./BookByPriceTraditionalView";
import BookByPriceSuperDOMView from "./BookByPriceSuperDOMView";
import "./BookByPricePanel.css";
import { PanelToolbar, PanelToolbarHost } from "./PanelToolbar";
import { buildBookAnnotationsByPrice } from "./BookByPriceAnnotations";
import PanelSymbolContextMenu from "./PanelSymbolContextMenu";
import RouterLoginModal from "../Modals/RouterLoginModal";
import { router } from "../../data/Services/ordersRouter";
import marketdata from "../../data/Services/marketdata";
import { buildOtoLegs } from "../../utils/otoLegs";
import { determineOrderType } from "../../utils/orderTypeLogic";
import { submitOrderToRouter } from "../../utils/orderSubmission";
import { normalizePrice } from "../../utils/priceUtils";
import {
  confirmNewOrder,
  confirmOrderModification,
  confirmOrderDeletion,
  showOrderConfirmationAsync,
} from "../../utils/confirmationUtils";
import logger from "../../utils/logger";

const DEFAULT_DEPTH = 40;

const MODES = [
  { key: "traditional", label: "Tradicional" },
  { key: "superdom", label: "SuperDOM" },
];

const BookByPricePanel = ({
  instanceId: instanceIdProp,
  initialState,
  initialToolbarPinned = true,
  onToolbarPinnedChange,
  onBookByPriceStateChange,
  claimFocus,
}) => {
  // instanceIdProp comes from DockModeLayout (FlexLayout node ID).
  // Fall back to a React-stable ID if not provided (e.g. in tests).
  const reactId = useId();
  const instanceId = instanceIdProp || reactId;
  const initialStateRef = useRef(initialState);

  // Initialize this instance in the store on mount.
  //
  // We DON'T remove the instance on unmount: FlexLayout unmounts panels on
  // tab visibility changes and on layout rebuilds (Organize-as-Tabs /
  // Organize-as-Grid). Removing the instance there would lose the symbol/
  // group/viewMode the group-link propagation fallback uses to update
  // unmounted panels, and on remount the panel would resurrect with stale
  // initialState (#334). The store entry lives for the session and is
  // implicitly cleared when a desktop is saved without this tab.
  useEffect(() => {
    useBookByPriceStore
      .getState()
      .initInstance(instanceId, initialStateRef.current);
  }, [instanceId]);

  // Per-instance state selectors
  const symbol = useBookByPriceStore(
    (s) =>
      s.instances[instanceId]?.symbol ??
      initialState?.symbol ??
      DEFAULT_BOOK_BY_PRICE_STATE.symbol,
  );
  const group = useBookByPriceStore(
    (s) => s.instances[instanceId]?.group ?? initialState?.group ?? null,
  );
  const viewMode = useBookByPriceStore(
    (s) =>
      s.instances[instanceId]?.viewMode ??
      initialState?.viewMode ??
      DEFAULT_BOOK_BY_PRICE_STATE.viewMode,
  );

  const setSymbol = useCallback(
    (sym) => useBookByPriceStore.getState().setInstanceSymbol(instanceId, sym),
    [instanceId],
  );
  const setViewMode = useCallback(
    (vm) => {
      useBookByPriceStore.getState().setInstanceViewMode(instanceId, vm);
      onBookByPriceStateChange?.({ symbol, group, viewMode: vm });
    },
    [group, instanceId, onBookByPriceStateChange, symbol],
  );

  const theme = useThemeStore((state) => state.theme);
  const isDarkMode = isDarkTheme(theme);
  const [toolbarPinned, setToolbarPinned] = useState(initialToolbarPinned);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pointerInsideContent, setPointerInsideContent] = useState(false);
  // const { isConnected: isRouterConnected } = useRouterStatus(); // SuperDOM sempre habilitado

  const contentRef = useRef(null);

  // Stable subscriber ID for the snapshot hook (separate from the store instance ID).
  const subscriberId = useMemo(() => `bookByPricePanel:${reactId}`, [reactId]);

  const isGroupUpdateRef = useRef(false);
  const prevSymbolRef = useRef(symbol);

  const handleGroupSymbolChange = useCallback(
    (newSymbol) => {
      const currentSymbol =
        useBookByPriceStore.getState().instances[instanceId]?.symbol;
      if (currentSymbol === newSymbol) return;
      isGroupUpdateRef.current = true;
      setSymbol(newSymbol);
      onBookByPriceStateChange?.({ symbol: newSymbol, group, viewMode });
    },
    [group, instanceId, onBookByPriceStateChange, setSymbol, viewMode],
  );

  const { emitSymbol } = useGroupLink(
    instanceId,
    group,
    handleGroupSymbolChange,
  );

  useEffect(() => {
    if (!symbol || prevSymbolRef.current === symbol) return;
    prevSymbolRef.current = symbol;
    if (!isGroupUpdateRef.current) emitSymbol(symbol);
    onBookByPriceStateChange?.({ symbol, group, viewMode });
    isGroupUpdateRef.current = false;
  }, [emitSymbol, group, onBookByPriceStateChange, symbol, viewMode]);

  const focusPanel = useCallback(() => {
    // Mark this book instance active (drives the navbar's book symbol), then claim
    // FlexLayout focus — setActiveTabset draws the border AND syncs focusedPanelType
    // through onModelChange → syncFocusFromModel (single source of truth). (#347)
    useBookByPriceStore.getState().setActiveInstance(instanceId);
    claimFocus?.();
  }, [instanceId, claimFocus]);

  const handleToolbarPinnedChange = useCallback(
    (pinned) => {
      setToolbarPinned(pinned);
      onToolbarPinnedChange?.(pinned);
    },
    [onToolbarPinnedChange],
  );

  useEffect(() => {
    if (viewMode === "traditional" && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [viewMode]);

  const { bids, asks, ladder, bestBid, bestAsk, midPrice, status } =
    useBookByPriceSnapshot(symbol, subscriberId, DEFAULT_DEPTH, {
      freezeGridCenter: viewMode === "superdom" && pointerInsideContent,
    });
  const { orders, updateOrderPrice, updateOTOPrice, removeOrder } =
    useOrderOverlay(symbol);
  const position = usePositionData(symbol);
  const { quantity } = useOrderDefaults(symbol);

  // Live market reference (book mid), mirrored into a ref so the order
  // handler reads a fresh value at click time without re-creating the
  // callback every book tick (which would re-render the memoized DOM view).
  const marketRefRef = useRef(null);
  useEffect(() => {
    const bidPx = Number(bestBid?.price) || 0;
    const askPx = Number(bestAsk?.price) || 0;
    marketRefRef.current =
      midPrice ?? (bidPx > 0 ? bidPx : askPx > 0 ? askPx : null);
  }, [midPrice, bestBid, bestAsk]);

  const handleSuperDomOrder = useCallback(
    async ({ side, price }) => {
      if (!symbol || !side || !price) return;

      if (router.state !== "online") {
        setShowLoginModal(true);
        return;
      }

      const orderId = crypto.randomUUID
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
      // Market reference for LIMIT/STOPLIMIT classification: the live book
      // mid (fresh, matches the ladder the user sees). The last-trade quote
      // can be stale for the DoM symbol and misclassifies an off-market
      // entry as LIMIT — the router then rejects the OTO bracket
      // ("preço ultrapassou entrada sugerida").
      const currentPrice =
        marketRefRef.current ?? marketdata.getLastQuote(symbol)?.C ?? null;
      const qty =
        useOrderDefaultsStore.getState().getAdjustedQuantity(symbol) ||
        quantity;
      const order = {
        id: orderId,
        symbol,
        side,
        quantity: qty,
        price,
        orderType: determineOrderType(side, price, currentPrice),
      };

      // Read OTO config from store at click time (not from closure) to avoid
      // stale values when the user configures OTO just before clicking.
      const otoConfig = useOrderDefaultsStore.getState().getOtoConfig(symbol);
      let legsBuilt = 0;
      if (otoConfig.enabled) {
        const legs = buildOtoLegs({
          mainOrderId: orderId,
          symbol,
          side,
          quantity: qty,
          anchorPrice: price,
          lossTicks: otoConfig.lossTicks,
          profitTicks: otoConfig.profitTicks,
        });
        legsBuilt = legs.length;
        if (legs.length) order.oto = legs;
      }

      try {
        const ods = useOrderDefaultsStore.getState();
        logger.diagnostic.info("[BookOTO] shift+click order built", {
          bookSymbol: symbol,
          side,
          price,
          qty,
          otoConfigEnabled: otoConfig.enabled,
          otoLossTicks: otoConfig.lossTicks,
          otoProfitTicks: otoConfig.profitTicks,
          legsBuilt,
          orderHasOto: Array.isArray(order.oto),
          otoEnabledKeys: Object.keys(ods.otoEnabled || {}),
          otoEnabledMap: ods.otoEnabled,
        });
      } catch {
        /* diagnostic best-effort */
      }

      // Honour the user's "confirmar ordens" setting, same as the chart
      // click-to-order path (ChartRenderer → confirmNewOrder). Resolves true
      // immediately when the setting is off, so express click-to-order is
      // unchanged for users who disabled confirmation.
      const confirmed = await confirmNewOrder(order);
      if (!confirmed) return;

      await submitOrderToRouter(
        order,
        () => setShowLoginModal(true),
        () => {},
        (error) => {
          alert(`Erro ao enviar ordem: ${error.message}`);
        },
      );
    },
    [quantity, symbol],
  );

  // Apply a batch of order moves — single drag, stack drag, or panel stepper.
  // One confirmation for the whole batch, then per-order optimistic update +
  // router replace (handled inside updateOrderPrice/updateOTOPrice).
  const handleMoveOrders = useCallback(
    async (moves) => {
      if (!Array.isArray(moves) || moves.length === 0) return;

      const resolved = moves
        .map((move) => {
          if (!move?.orderId || !Number.isFinite(move.price)) return null;
          const order = orders.find((item) => item.id === move.orderId);
          if (!order) return null;
          const normalizedPrice = normalizePrice(symbol, move.price);
          const current = move.otoOrderId
            ? order.oto?.find((leg) => leg.id === move.otoOrderId)
            : order;
          if (!current) return null;
          const fromPrice = current.price ?? current.limit ?? current.stop;
          // Drop no-op moves (target equals the current price).
          if (normalizePrice(symbol, fromPrice) === normalizedPrice)
            return null;
          return { ...move, order, price: normalizedPrice, fromPrice };
        })
        .filter(Boolean);
      if (resolved.length === 0) return;

      const needsConfirm = resolved.some(
        (m) => m.order.state === "open" || m.order.state === "pending",
      );
      if (needsConfirm) {
        let confirmed;
        if (resolved.length === 1) {
          const m = resolved[0];
          confirmed = await confirmOrderModification(
            {
              ...m.order,
              price: m.fromPrice,
              symbol: m.order.symbol || symbol,
            },
            m.price,
          );
        } else {
          confirmed = await showOrderConfirmationAsync({
            message: `Mover ${resolved.length} ordens neste preço?`,
            header: "Confirmar Modificação",
            icon: "pi pi-pencil",
            acceptLabel: "Mover",
            rejectLabel: "Cancelar",
          });
        }
        if (!confirmed) return;
      }

      for (const m of resolved) {
        if (m.otoOrderId) {
          await updateOTOPrice(m.orderId, m.otoOrderId, m.price);
        } else {
          await updateOrderPrice(m.orderId, m.price);
        }
      }
    },
    [orders, symbol, updateOrderPrice, updateOTOPrice],
  );

  // Cancel an order from its [×] marker — store removal for local, router cancel
  // for open/pending (confirmed first, matching the chart's cancel path).
  const handleCancelAnnotation = useCallback(
    async ({ orderId }) => {
      if (!orderId) return;
      const order = orders.find((item) => item.id === orderId);
      if (!order) return;

      if (order.state === "open" || order.state === "pending") {
        const confirmed = await confirmOrderDeletion({
          ...order,
          symbol: order.symbol || symbol,
        });
        if (!confirmed) return;
      }

      await removeOrder(orderId);
    },
    [orders, symbol, removeOrder],
  );

  // Pointer-based drag for SuperDOM order markers — single or stacked.
  const { dragState, startDrag, startStackDrag, isDragging } = useBookOrderDrag(
    {
      symbol,
      orders,
      onCommit: handleMoveOrders,
    },
  );

  // Live drag-preview prices, keyed by orderId (store stays untouched mid-drag).
  const priceOverrides = dragState?.overrides || undefined;

  const annotationsByPrice = useMemo(
    () =>
      buildBookAnnotationsByPrice({ symbol, orders, position, priceOverrides }),
    [orders, position, symbol, priceOverrides],
  );

  // SuperDOM mantido SEMPRE habilitado — gate de "router offline" desativado.
  // (envio de ordem ainda checa router.state e abre o RouterLoginModal.)
  // const domVisible = status === STATUS.LIVE;
  // const showRouterPrompt =
  //   viewMode === "superdom" && domVisible && !isRouterConnected;
  const showRouterPrompt = false;

  const renderView = () => {
    if (status === STATUS.IDLE) {
      return (
        <div className="bbp-placeholder">
          Selecione um símbolo para visualizar o livro.
        </div>
      );
    }
    if (status === STATUS.LOADING) {
      return (
        <div className="bbp-placeholder">Carregando livro de ofertas…</div>
      );
    }
    if (status === STATUS.EMPTY) {
      return <div className="bbp-placeholder">Sem ofertas no momento.</div>;
    }
    if (status === STATUS.ERROR) {
      return (
        <div className="bbp-placeholder">
          Erro ao receber o livro de ofertas.
        </div>
      );
    }

    if (viewMode === "superdom") {
      return (
        <BookByPriceSuperDOMView
          contentRef={contentRef}
          symbol={symbol}
          ladder={ladder}
          bestBid={bestBid}
          bestAsk={bestAsk}
          depth={DEFAULT_DEPTH}
          viewMode={viewMode}
          status={status}
          freezeAutoCenter={pointerInsideContent || isDragging}
          annotationsByPrice={annotationsByPrice}
          onOrder={handleSuperDomOrder}
          onAnnotationPointerDown={startDrag}
          onStackPointerDown={startStackDrag}
          onAnnotationCancel={handleCancelAnnotation}
          onMoveOrders={handleMoveOrders}
          isDragging={isDragging}
          draggingOrderIds={dragState?.orderIds ?? null}
          interactionDisabled={showRouterPrompt}
        />
      );
    }
    return (
      <BookByPriceTraditionalView symbol={symbol} bids={bids} asks={asks} />
    );
  };

  return (
    <div
      className={`bbp-panel ${isDarkMode ? "bbp-panel--dark" : ""}`}
      onMouseDown={focusPanel}
      data-evo-id="bookbypricepanel-root"
    >
      <PanelToolbarHost
        pinned={toolbarPinned}
        contentClassName="bbp-toolbar-content"
        toolbar={
          <PanelToolbar
            pinned={toolbarPinned}
            onPinnedChange={handleToolbarPinnedChange}
            className="bbp-header"
            pinDataEvoId="bookbypricepanel-btn-toolbar-pin"
          >
            <div className="bbp-mode" role="tablist">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={viewMode === m.key}
                  className={`bbp-mode__btn ${viewMode === m.key ? "bbp-mode__btn--active" : ""}`}
                  onClick={() => setViewMode(m.key)}
                  data-evo-id={`bookbypricepanel-mode-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </PanelToolbar>
        }
      >
        <div className="bbp-content-area">
          <div
            className="bbp-content"
            ref={contentRef}
            onPointerEnter={() => setPointerInsideContent(true)}
            onPointerLeave={() => setPointerInsideContent(false)}
          >
            {renderView()}
          </div>
          <PanelSymbolContextMenu
            targetRef={contentRef}
            panelType="bookByPrice"
            label="Alterar símbolo…"
            icon="pi pi-search"
          />
          {showRouterPrompt && (
            <div
              className="bbp-router-overlay"
              data-evo-id="bookbypricepanel-router-overlay"
            >
              <a
                href="#"
                className="bbp-router-overlay__link"
                onClick={(e) => {
                  e.preventDefault();
                  setShowLoginModal(true);
                }}
                data-evo-id="bookbypricepanel-link-router-login"
              >
                Conecte-se ao roteador
              </a>
            </div>
          )}
        </div>
      </PanelToolbarHost>
      <RouterLoginModal
        visible={showLoginModal}
        onHide={() => setShowLoginModal(false)}
      />
    </div>
  );
};

BookByPricePanel.propTypes = {
  instanceId: PropTypes.string,
  initialState: PropTypes.shape({
    symbol: PropTypes.string,
    group: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    viewMode: PropTypes.string,
  }),
  initialToolbarPinned: PropTypes.bool,
  onToolbarPinnedChange: PropTypes.func,
  onBookByPriceStateChange: PropTypes.func,
  claimFocus: PropTypes.func,
};

export default React.memo(BookByPricePanel);
